// The wind tunnel panel: fly the plan twenty times before you commit to it once.
//
// The planning stage asks the player to bet a battalion on a plan they have never seen run. This panel
// runs it — headless, in a worker, against twenty different sets of enemy dice — and answers with a
// distribution rather than a verdict: how often the objective falls, how long it takes when it does,
// what it costs, when contact comes, and the three things that most often go wrong. Then it does the
// same for an advisor's opening so the two can be read side by side.
//
// Everything here is a card, a chip or a bar (spec §8): no dropdowns, no modal, nothing that bounces.
// The one insertion point in src/ui/stages/PlanningStage.tsx is `<WindTunnel />`, directly below the
// plan; `<WindTunnelChip />` is the one insertion in the live stage's top bar.

import { useMemo } from 'react';
import { useStore } from '../../state/store';
import { useAdvisors } from '../../state/advisors';
import {
  PERSONA_LABEL, SEED_COUNT, advisorPlan, summarize, useWindTunnel,
  type BatchRunState, type BatchSummary, type Band,
} from '../../state/windtunnel';
import { PERSONA_IDS, type Persona } from '../../ai/prompts';
import { clockHM } from '../reports';
import { AcronymText } from '../Acronym';
import { Chip } from '../cards/ui';

/** The three outcomes, in the order the bar draws them. */
const OUTCOMES = [
  { key: 'seized', label: 'seized', color: 'var(--color-friendly)', title: 'The objective was taken and held clear of the enemy for five minutes.' },
  { key: 'culminated', label: 'culminated', color: 'var(--color-warn)', title: 'The force ran out of fuel, ammunition or will before it got there.' },
  { key: 'time', label: 'out of time', color: 'var(--color-fog)', title: 'The clock ran out with the objective still held by the enemy.' },
] as const;

function outcomeCount(s: BatchSummary, key: (typeof OUTCOMES)[number]['key']): number {
  return key === 'seized' ? s.seized : key === 'culminated' ? s.culminated : s.outOfTime;
}

export function WindTunnel() {
  const planOrders = useStore((s) => s.plan);
  const running = useWindTunnel((s) => s.running);
  const progress = useWindTunnel((s) => s.progress);
  const error = useWindTunnel((s) => s.error);
  const plan = useWindTunnel((s) => s.plan);
  const compare = useWindTunnel((s) => s.compare);
  const testPlan = useWindTunnel((s) => s.testPlan);

  const noOrders = planOrders.length === 0;
  const busy = running !== null;
  const disabledReason = noOrders
    ? 'Task at least one unit before testing the plan.'
    : busy
      ? 'A test is already running.'
      : null;

  return (
    <section data-testid="windtunnel-panel" className="wt-panel">
      <header className="wt-panel-head">
        <span>Wind tunnel</span>
        <span className="tracking-normal">{SEED_COUNT} runs</span>
      </header>
      <div className="space-y-2 p-2">
        <p className="mono text-[10px] leading-snug text-fog">
          Runs your plan {SEED_COUNT} times against varied enemy dice; this is the wind tunnel.
        </p>

        <button
          type="button"
          data-testid="test-plan"
          disabled={disabledReason !== null}
          onClick={() => void testPlan()}
          title={disabledReason ?? `Fly this plan ${SEED_COUNT} times and show the spread of outcomes`}
          className="mono w-full rounded-sm border px-2 py-1.5 text-[11px] uppercase tracking-[0.2em]"
          style={{
            borderColor: disabledReason ? 'var(--color-slate-700)' : 'var(--color-friendly)',
            color: disabledReason ? 'var(--color-fog)' : 'var(--color-friendly)',
            background: disabledReason ? 'transparent' : 'color-mix(in srgb, var(--color-friendly) 12%, transparent)',
            cursor: disabledReason ? 'default' : 'pointer',
          }}
        >
          {running === 'plan' ? 'testing…' : 'Test this plan'}
        </button>
        {disabledReason && !busy && (
          <p data-testid="windtunnel-disabled" className="mono text-[10px] leading-snug text-fog">{disabledReason}</p>
        )}

        {busy && <Progress done={progress.done} total={progress.total} subject={running === 'plan' ? 'your plan' : PERSONA_LABEL[running as Persona]} />}
        {error && <p data-testid="windtunnel-error" className="mono text-[10px] leading-snug text-hostile">{error}</p>}

        {plan && <Results run={plan} compare={compare} />}
        {plan && <CompareChips busy={busy} />}
      </div>
    </section>
  );
}

// ---- progress -------------------------------------------------------------------------------------

/** A bar that fills once and stops. No indeterminate shimmer: it knows exactly how far along it is. */
function Progress({ done, total, subject }: { done: number; total: number; subject: string }) {
  const pct = total > 0 ? (done / total) * 100 : 0;
  return (
    <div data-testid="windtunnel-progress" className="space-y-1">
      <div className="flex items-baseline justify-between">
        <span className="mono text-[10px] text-fog">flying {subject}</span>
        <span className="mono text-[10px] text-paper">{done}/{total}</span>
      </div>
      <span className="block h-[3px] rounded-full" style={{ background: 'var(--color-slate-700)' }}>
        <span
          className="block h-full rounded-full"
          style={{ width: `${pct}%`, background: 'var(--color-friendly)', transition: 'width 150ms linear' }}
        />
      </span>
    </div>
  );
}

// ---- one batch's results ---------------------------------------------------------------------------

function Results({ run, compare }: { run: BatchRunState; compare: BatchRunState | null }) {
  const summary = summarize(run.results);
  const other = compare ? summarize(compare.results) : null;

  return (
    <div data-testid="windtunnel-results" className="space-y-2">
      <OutcomeBar summary={summary} testId="windtunnel-outcome" />

      {other && compare && (
        <div className="space-y-1.5 border-t border-slate-700 pt-2">
          <div className="flex items-baseline justify-between gap-2">
            <span className="mono text-[9px] uppercase tracking-[0.2em] text-fog">side by side</span>
            <Delta mine={summary.seized} theirs={other.seized} />
          </div>
          <CompareRow label="your plan" summary={summary} />
          <CompareRow label={compare.label} summary={other} testId="windtunnel-compare-bar" />
        </div>
      )}

      <dl className="space-y-1">
        <Row
          label="Time to objective"
          value={fmtBand(summary.timeToObjective, clockHM)}
          note={summary.timeToObjective ? `${summary.timeToObjective.n} of ${summary.total} runs got there` : 'no run reached it'}
          title="The 10th to 90th percentile of the runs that put a platoon on the objective, measured from H-hour."
        />
        <Row
          label="Blue losses"
          value={fmtBand(summary.casualties, (v) => String(v))}
          note="platoons destroyed or broken"
          title="Platoons destroyed outright plus platoons that broke and withdrew, 10th to 90th percentile."
        />
        <Row
          label="First contact"
          value={fmtBand(summary.firstContact, clockHM)}
          note={summary.firstContact ? `${summary.firstContact.n} of ${summary.total} runs made contact` : 'never found the enemy'}
          title="When the first enemy contact was reported to your headquarters, 10th to 90th percentile."
        />
      </dl>

      <div>
        <div className="mono text-[9px] uppercase tracking-[0.2em] text-fog">What goes wrong</div>
        {summary.causes.length === 0 ? (
          <p className="mono mt-1 text-[10px] text-fog">Nothing recurring. The plan costs no time it did not mean to spend.</p>
        ) : (
          <ul data-testid="windtunnel-causes" className="mt-1 space-y-1">
            {summary.causes.map((c) => (
              <li key={c.item} className="flex items-start gap-2">
                <span className="mono min-w-0 flex-1 text-[10px] leading-snug text-paper">
                  <AcronymText>{c.item}</AcronymText>
                </span>
                <span className="mono shrink-0 text-[10px]" style={{ color: 'var(--color-warn)' }}>
                  {c.count}/{summary.total}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

/** The three-segment outcome bar with its counts underneath. */
function OutcomeBar({ summary, testId }: { summary: BatchSummary; testId?: string }) {
  return (
    <div data-testid={testId}>
      <div className="flex h-2 w-full overflow-hidden rounded-sm" style={{ background: 'var(--color-slate-700)' }}>
        {OUTCOMES.map((o) => {
          const n = outcomeCount(summary, o.key);
          if (n === 0) return null;
          return (
            <span
              key={o.key}
              title={`${n} of ${summary.total}: ${o.title}`}
              style={{ flexGrow: n, background: o.color, opacity: o.key === 'time' ? 0.7 : 1 }}
            />
          );
        })}
      </div>
      <div className="mt-1 flex flex-wrap items-baseline gap-x-2.5 gap-y-0.5">
        {OUTCOMES.map((o) => (
          <span key={o.key} className="mono text-[10px]" title={o.title}>
            <span data-testid={`windtunnel-count-${o.key}`} style={{ color: o.color }}>{outcomeCount(summary, o.key)}</span>
            <span className="ml-1 text-fog">{o.label}</span>
          </span>
        ))}
      </div>
    </div>
  );
}

/** One plan's bar in the side-by-side, at half height with its name on the left. */
function CompareRow({ label, summary, testId }: { label: string; summary: BatchSummary; testId?: string }) {
  return (
    <div data-testid={testId} className="flex items-center gap-2">
      <span className="mono w-[86px] shrink-0 truncate text-[10px] text-fog" title={label}>{label}</span>
      <span className="flex h-1.5 flex-1 overflow-hidden rounded-sm" style={{ background: 'var(--color-slate-700)' }}>
        {OUTCOMES.map((o) => {
          const n = outcomeCount(summary, o.key);
          if (n === 0) return null;
          return <span key={o.key} style={{ flexGrow: n, background: o.color, opacity: o.key === 'time' ? 0.7 : 1 }} />;
        })}
      </span>
      <span className="mono w-[42px] shrink-0 text-right text-[10px]" style={{ color: 'var(--color-friendly)' }}>
        {summary.seized}/{summary.total}
      </span>
    </div>
  );
}

/** The one number the comparison is about: how many more (or fewer) runs your plan seizes. */
function Delta({ mine, theirs }: { mine: number; theirs: number }) {
  const d = mine - theirs;
  const color = d > 0 ? 'var(--color-friendly)' : d < 0 ? 'var(--color-hostile)' : 'var(--color-fog)';
  const text = d === 0 ? 'no difference' : `${d > 0 ? '+' : '−'}${Math.abs(d)} seized for your plan`;
  return (
    <span
      data-testid="windtunnel-delta"
      className="mono rounded-sm border px-1.5 py-0.5 text-[10px]"
      style={{ borderColor: color, color }}
      title="The difference in seizures over the same twenty enemies."
    >
      {text}
    </span>
  );
}

function Row({ label, value, note, title }: { label: string; value: string; note: string; title: string }) {
  return (
    <div className="flex items-baseline gap-2" title={title}>
      <dt className="mono w-[86px] shrink-0 text-[10px] text-fog">{label}</dt>
      <dd className="mono min-w-0 flex-1 text-[10px] text-paper">
        {value}
        <span className="ml-1.5 text-fog">{note}</span>
      </dd>
    </div>
  );
}

function fmtBand(b: Band | null, fmt: (v: number) => string): string {
  if (!b) return '—';
  return b.p10 === b.p90 ? fmt(b.p10) : `${fmt(b.p10)} – ${fmt(b.p90)}`;
}

// ---- comparison chips ------------------------------------------------------------------------------

/** One chip per advisor. Each flies that advisor's opening play against the same twenty enemies. */
function CompareChips({ busy }: { busy: boolean }) {
  const testAdvisor = useWindTunnel((s) => s.testAdvisor);
  const compare = useWindTunnel((s) => s.compare);
  const running = useWindTunnel((s) => s.running);
  // Building a template play costs a terrain search per order, and this component re-renders on every
  // one of the twenty progress ticks; the chips only need to know how many orders each play has.
  const plays = useAdvisors((s) => s.plays);
  const scenario = useStore((s) => s.scenario);
  const sizes = useMemo(
    () => Object.fromEntries(PERSONA_IDS.map((p) => [p, advisorPlan(p).orders.length])) as Record<Persona, number>,
    [plays, scenario],
  );

  return (
    <div className="border-t border-slate-700 pt-2">
      <div className="mono text-[9px] uppercase tracking-[0.2em] text-fog">Compare against</div>
      <div className="mt-1 flex flex-wrap gap-1">
        {PERSONA_IDS.map((p) => {
          const n = sizes[p];
          return (
            <Chip
              key={p}
              testId={`windtunnel-compare-${p}`}
              tone="friendly"
              active={compare?.subject === p}
              disabled={busy || n === 0}
              onClick={() => void testAdvisor(p)}
              title={
                n === 0
                  ? `The ${PERSONA_LABEL[p]} has no play to test right now.`
                  : `Fly the ${PERSONA_LABEL[p]}'s opening play (${n} order${n === 1 ? '' : 's'}) against the same ${SEED_COUNT} enemies`
              }
            >
              {running === p ? `${p}…` : p}
            </Chip>
          );
        })}
      </div>
    </div>
  );
}

// ---- the live stage's reminder ----------------------------------------------------------------------

/**
 * "tested: 14/20 seized" — what the wind tunnel said about the plan now being fought, sitting next to
 * the phase in the live stage's top bar. Renders nothing when the plan was never tested.
 */
export function WindTunnelChip() {
  const plan = useWindTunnel((s) => s.plan);
  if (!plan) return null;
  const s = summarize(plan.results);
  return (
    <span
      data-testid="windtunnel-live-chip"
      className="mono rounded-sm border px-1.5 py-0.5 text-[10px] uppercase tracking-[0.12em] whitespace-nowrap"
      style={{ borderColor: 'var(--color-slate-700)', color: 'var(--color-fog)' }}
      title={`Before H-hour this plan was flown ${s.total} times against varied enemy dice: ${s.seized} seized, ${s.culminated} culminated, ${s.outOfTime} ran out of time.`}
    >
      tested: <span style={{ color: 'var(--color-friendly)' }}>{s.seized}/{s.total}</span> seized
    </span>
  );
}
